import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type {
  PrizeHistoryItem,
  RouletteDetail,
  RoulettePrize,
  SpinResult,
} from '@/types/roulette';
import { RouletteSpinError } from '~/adapters/api-roulette/roulette-api';
import { fetchRoulettesHandler } from '~/handlers/fetch-roulettes.handler';
import { fetchRouletteDetailHandler } from '~/handlers/fetch-roulette-detail.handler';
import { postRouletteSpinHandler } from '~/handlers/post-roulette-spin.handler';
import { fetchRoulettePrizeHistoryHandler } from '~/handlers/fetch-roulette-prize-history.handler';
import { tryCatch } from '~/utils/apostatudo/try-catch';

// Roleta antiga (fallback): minigame do Smartico servido quando o core não tem
// roleta pra esse user. O id vem do deep-link (/mini-games/6606); esse é o
// default quando a roletinha é aberta sem id (ex.: pelo menu).
const LEGACY_ROULETTE_MINIGAME_ID = 6606;

export type RouletteStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'spinning'
  | 'error'
  | 'unavailable';

export type RouletteHistoryStatus =
  | 'idle'
  | 'loading'
  | 'loadingMore'
  | 'ready'
  | 'error';

export const useRouletteStore = defineStore('roulette', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  const { userId } = useUserData();

  // Abertura do modal global (gatilho via store, sem navegar de rota).
  const isOpen = ref(false);

  // Id da roleta antiga (Smartico) usada como fallback quando o core não tem
  // roleta pra esse user. Setado no open() a partir do id do link.
  const fallbackMiniGameId = ref(LEGACY_ROULETTE_MINIGAME_ID);

  const status = ref<RouletteStatus>('idle');
  const detail = ref<RouletteDetail | null>(null);
  const errorMessage = ref<string | null>(null);

  const lastSpinResult = ref<SpinResult | null>(null);

  // Histórico de prêmios (paginado).
  const historyItems = ref<PrizeHistoryItem[]>([]);
  const historyStatus = ref<RouletteHistoryStatus>('idle');
  const historyPage = ref(0);
  const historyLastPage = ref(1);
  const canLoadMoreHistory = computed(
    () => historyPage.value < historyLastPage.value,
  );

  const prizes = computed<RoulettePrize[]>(() =>
    [...(detail.value?.prizes ?? [])].sort((a, b) => a.position - b.position),
  );

  const availableSpins = computed(() => detail.value?.availableSpins ?? 0);
  const nextSpinAvailableAt = computed(
    () => detail.value?.nextSpinAvailableAt ?? null,
  );

  const slug = computed(() => detail.value?.slug ?? '');
  const canSpin = computed(
    () => status.value === 'ready' && availableSpins.value > 0 && !!slug.value,
  );

  function reset() {
    status.value = 'idle';
    detail.value = null;
    errorMessage.value = null;
    lastSpinResult.value = null;
    historyItems.value = [];
    historyStatus.value = 'idle';
    historyPage.value = 0;
    historyLastPage.value = 1;
  }

  function open(opts?: { fallbackMiniGameId?: number }) {
    const id = opts?.fallbackMiniGameId;

    fallbackMiniGameId.value =
      typeof id === 'number' && !Number.isNaN(id)
        ? id
        : LEGACY_ROULETTE_MINIGAME_ID;
    isOpen.value = true;
  }

  // Sem roleta no core pra esse user: fecha o sheet novo e navega pra roleta
  // antiga (iframe do Smartico inline, como era antes do ENG-375). Reusa a rota
  // /mini-games/[id] via ?legacy=1 — rotas novas são bloqueadas no deploy de
  // prod. O id do fallback vem do link que abriu a roletinha.
  function openLegacyRoulette() {
    close();
    navigateTo({
      path: localeLink(`/mini-games/${fallbackMiniGameId.value}`),
      query: { legacy: '1' },
    });
  }

  function close() {
    isOpen.value = false;
  }

  function captureUnexpected(error: unknown, source: string) {
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      { tags: { source } },
    );
  }

  // Resolve a roleta do usuário: lista do segmento → data[0]. Sem itens ⇒ indisponível.
  async function init() {
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    if (!userId.value) {
      // autenticado, mas o perfil ainda não carregou (GET /auth/user-profile)
      status.value = 'error';
      errorMessage.value =
        'Não foi possível carregar seus dados. Tente novamente.';

      return;
    }

    status.value = 'loading';
    errorMessage.value = null;

    const [list, listError] = await tryCatch(
      fetchRoulettesHandler({ userId: userId.value }),
    );

    if (listError || !list) {
      status.value = 'error';
      errorMessage.value = 'Serviço indisponível no momento. Tente novamente.';
      captureUnexpected(listError, 'roulette.init.list');

      return;
    }

    const first = list.data[0];

    if (!first) {
      // Sem roleta no core: em vez do beco sem saída ('unavailable'), abre a
      // roleta antiga do Smartico. (Erro de rede/serviço acima segue na tela de
      // erro — isso aqui é só o caso legítimo de "user sem roleta".)
      openLegacyRoulette();

      return;
    }

    await loadDetail(first.slug);
  }

  // silent: refresh de fundo (inatividade/retomada de aba). Uma falha transitória
  // não pode derrubar o modal pra tela de erro — mantém o último detalhe bom.
  async function loadDetail(
    targetSlug: string,
    options?: { silent?: boolean },
  ) {
    if (!userId.value) return;

    const [data, detailError] = await tryCatch(
      fetchRouletteDetailHandler({ slug: targetSlug, userId: userId.value }),
    );

    if (detailError || !data) {
      if (options?.silent) return;

      status.value = 'error';
      errorMessage.value = 'Serviço indisponível no momento. Tente novamente.';
      captureUnexpected(detailError, 'roulette.loadDetail');

      return;
    }

    detail.value = data;
    status.value = 'ready';
  }

  // Gira a roleta. Retorna o resultado para o componente animar a roda até a
  // position e só então abrir o popup. O sorteio é server-authoritative.
  async function spin(): Promise<SpinResult | null> {
    if (!canSpin.value || !userId.value) return null;

    status.value = 'spinning';
    errorMessage.value = null;

    const [result, spinError] = await tryCatch(
      postRouletteSpinHandler({ slug: slug.value, userId: userId.value }),
    );

    if (spinError || !result) {
      status.value = 'ready';

      // O adapter classifica a falha (kind); mensagem e triagem decidem aqui.
      const spinErr = spinError instanceof RouletteSpinError ? spinError : null;

      errorMessage.value =
        spinErr?.kind === 'rate_limited'
          ? 'Muitas tentativas. Aguarde um momento e tente novamente.'
          : spinErr?.kind === 'network'
            ? 'Não foi possível conectar. Tente novamente.'
            : spinErr?.message || 'Erro ao girar a roleta.';

      // 429, queda de rede e delivery_failed (giro estornado, retryável) são
      // esperados; só erro da API/desconhecido interessa à triagem do Sentry.
      if (!spinErr || spinErr.kind === 'api') {
        captureUnexpected(spinError, 'roulette.spin');
      }

      return null;
    }

    lastSpinResult.value = result;

    if (detail.value) {
      detail.value.availableSpins = result.spinsRemaining;
      // Entrega síncrona: o /spin já devolve o próximo horário — atualiza o
      // countdown na hora (sem esperar o refetch do detalhe).
      detail.value.nextSpinAvailableAt = result.nextSpinAvailableAt;
    }

    return result;
  }

  // Chamado pelo componente após a animação da roda terminar.
  function settleSpin() {
    status.value = 'ready';
  }

  // Histórico de prêmios (all-time, paginado). loadHistory recarrega a 1ª página.
  async function loadHistory() {
    if (!userId.value) return;

    historyStatus.value = 'loading';

    const [page, error] = await tryCatch(
      fetchRoulettePrizeHistoryHandler({ userId: userId.value, page: 1 }),
    );

    if (error || !page) {
      historyStatus.value = 'error';
      captureUnexpected(error, 'roulette.history');

      return;
    }

    historyItems.value = page.items;
    historyPage.value = page.currentPage;
    historyLastPage.value = page.lastPage;
    historyStatus.value = 'ready';
  }

  async function loadMoreHistory() {
    if (
      !userId.value ||
      !canLoadMoreHistory.value ||
      historyStatus.value === 'loadingMore'
    ) {
      return;
    }

    historyStatus.value = 'loadingMore';

    const [page, error] = await tryCatch(
      fetchRoulettePrizeHistoryHandler({
        userId: userId.value,
        page: historyPage.value + 1,
      }),
    );

    if (error || !page) {
      historyStatus.value = 'ready';

      return;
    }

    historyItems.value = [...historyItems.value, ...page.items];
    historyPage.value = page.currentPage;
    historyLastPage.value = page.lastPage;
    historyStatus.value = 'ready';
  }

  return {
    isOpen,
    status,
    detail,
    errorMessage,
    lastSpinResult,
    prizes,
    availableSpins,
    nextSpinAvailableAt,
    slug,
    canSpin,
    historyItems,
    historyStatus,
    canLoadMoreHistory,
    open,
    close,
    init,
    loadDetail,
    spin,
    settleSpin,
    loadHistory,
    loadMoreHistory,
    reset,
  };
});
